import Drawer from '@frontend/components/drawer/Drawer'
import useAuth from '@frontend/hooks/useAuth'
import useSiteConfiguration from '@frontend/libs/hooks/useSiteConfiguration'
import { Bars3Icon } from '@heroicons/react/24/outline'
import { Button } from '@nextui-org/react'
import { useConfirmationDialog } from '@shared/features/confirm-modal/ConfirmationDialog'
import { __ } from '@wordpress/i18n'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router-dom'

import { useLogout } from '../authentication/lib/hook'
import AsavaMegaMenu from './components/mega-menu/AsavaMegaMenu'
import AsvMegaMenu from './components/mega-menu/AsvMegaMenu'
import CollaborationMegaMenu from './components/mega-menu/CollaborationMegaMenu'
import GroupMegaMenu from './components/mega-menu/GroupMegaMenu'
import ProjectMegaMenu from './components/mega-menu/ProjectMegaMenu'
import NavMenuItem from './components/NavMenuItem'
import NestedMenuDrawer from './components/NestedMenuDrawer'

function MobileMenuDrawer() {
  const { isLoggedIn } = useAuth()

  const { menus } = useSiteConfiguration()
  const footerMenu = menus?.footer.filter((menu) => menu.title !== 'ASAVAGROUP')

  const [isOpen, setIsOpen] = useState(false)

  const location = useLocation()

  useEffect(() => {
    return () => {
      setIsOpen(false)
    }
  }, [location])

  const { getConfirmation } = useConfirmationDialog()
  const { mutate } = useLogout()

  async function handleLogout() {
    const confirmed = await getConfirmation({
      body: 'Are you sure you want to logout?',
      confirmButtonProps: {
        color: 'primary',
      },
      confirmText: 'Logout',
      dismissButtonProps: {
        color: 'default',
      },
      dismissText: 'Cancel',
      header: 'Logout Confirmation!',
    })

    if (!confirmed) {
      return
    }

    await toast.promise(mutate(null), {
      error: (error) => error?.message || 'Failed to logout',
      loading: 'Logging out...',
      success: 'Logged out successfully',
    })

    window.location.href = '/'
  }

  return (
    <>
      <Button
        color="primary"
        isIconOnly
        onPress={() => setIsOpen(true)}
        variant="light"
      >
        <Bars3Icon
          className="block h-10 w-10 !max-w-none"
          style={{
            strokeWidth: 0.5,
          }}
        />
      </Button>
      <Drawer
        isFullWidth
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        position="right"
      >
        <Drawer.Content>
          <nav className="mt-20 main-navigation">
            <ul>
              {menus?.primary.map((menu) => (
                <li className="menu-item" key={menu.id}>
                  {menu.description === 'mega-menu.asava' && (
                    <NestedMenuDrawer menu={menu}>
                      <AsavaMegaMenu />
                    </NestedMenuDrawer>
                  )}
                  {menu.description === 'mega-menu.asv' && (
                    <NestedMenuDrawer menu={menu}>
                      <AsvMegaMenu />
                    </NestedMenuDrawer>
                  )}
                  {menu.description === 'mega-menu.collaboration' && (
                    <NestedMenuDrawer menu={menu}>
                      <CollaborationMegaMenu />
                    </NestedMenuDrawer>
                  )}
                  {menu.description === 'mega-menu.group' && (
                    <NestedMenuDrawer menu={menu}>
                      <GroupMegaMenu />
                    </NestedMenuDrawer>
                  )}
                  {menu.description === 'mega-menu.projects' && (
                    <NestedMenuDrawer menu={menu}>
                      <ProjectMegaMenu />
                    </NestedMenuDrawer>
                  )}
                  {!menu.description && (
                    <NavMenuItem
                      asLink
                      target={menu.target}
                      title={menu.title}
                      to={menu.url || '#'}
                    />
                  )}
                </li>
              ))}
              <li className="py-16" />
              <li>
                {!isLoggedIn && (
                  <NavMenuItem
                    asLink
                    title={__('Sign In', 'asavagroup')}
                    to={'/my-account'}
                  />
                )}
                {isLoggedIn && (
                  <NestedMenuDrawer
                    menu={{
                      description: '',
                      id: 9999,
                      target: '',
                      title: __('My Account', 'asavagroup'),
                      url: '/my-account',
                    }}
                  >
                    <ul>
                      <li>
                        <NavMenuItem
                          asLink
                          title={__('Personal Information', 'asavagroup')}
                          to={'/my-account/edit-account'}
                        />
                      </li>
                      <li>
                        <NavMenuItem
                          asLink
                          title={__('Order History', 'asavagroup')}
                          to={'/my-account/orders'}
                        />
                      </li>
                      {/* <li>
                                                <NavMenuItem
                                                    asLink
                                                    title={__('Payment History', 'asavagroup')}
                                                    to={`/my-account/payment-history`}
                                                />
                                            </li> */}
                      {/* <li>
                                                <NavMenuItem
                                                    asLink
                                                    title={__('Coupons', 'asavagroup')}
                                                    to={`/my-account/coupons`}
                                                />
                                            </li>
                                            <li>
                                                <NavMenuItem
                                                    asLink
                                                    title={__('Points', 'asavagroup')}
                                                    to={`/my-account/points-and-rewards`}
                                                />
                                            </li>
                                            <li>
                                                <NavMenuItem
                                                    asLink
                                                    title={__('Shipping Addresses', 'asavagroup')}
                                                    to={`/my-account/edit-address`}
                                                />
                                            </li>
                                            <li>
                                                <NavMenuItem
                                                    asLink
                                                    title={__('Billing Addresses', 'asavagroup')}
                                                    to={`/my-account/edit-address`}
                                                />
                                            </li> */}
                      <li>
                        <NavMenuItem
                          onPress={handleLogout}
                          title={__('Logout', 'asavagroup')}
                        />
                      </li>
                    </ul>
                  </NestedMenuDrawer>
                )}
              </li>
              <li>
                <NavMenuItem
                  asLink
                  title={__('Store Locator', 'asavagroup')}
                  to={`/stores`}
                />
              </li>
              {footerMenu?.map((menu) => (
                <li key={menu.title}>
                  <NestedMenuDrawer menu={menu}>
                    <ul>
                      {menu.items.map((item) => (
                        <li key={item.id}>
                          <NavMenuItem
                            asLink
                            target={item.target}
                            title={item.title}
                            to={item.url || '#'}
                          />
                        </li>
                      ))}
                    </ul>
                  </NestedMenuDrawer>
                </li>
              ))}
            </ul>
          </nav>
        </Drawer.Content>
      </Drawer>
    </>
  )
}

export default MobileMenuDrawer
